import React, { useRef, useEffect, useState, useCallback } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Input } from '../ui/input';
import { MapPin, Loader2, Search, AlertTriangle } from 'lucide-react';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';

const mapContainerStyle = {
  width: '100%',
  height: '250px',
  borderRadius: '8px'
};

const defaultCenter = {
  lat: 41.3275,
  lng: 19.8187
};

const LocationPicker = ({ 
  value = {},
  onChange,
  placeholder = "Shkruani adresën..."
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  
  const [inputValue, setInputValue] = useState(value.address || '');
  const [markerPosition, setMarkerPosition] = useState(
    value.latitude && value.longitude 
      ? { lat: parseFloat(value.latitude), lng: parseFloat(value.longitude) }
      : null
  );
  const [mapCenter, setMapCenter] = useState(
    value.latitude && value.longitude 
      ? { lat: parseFloat(value.latitude), lng: parseFloat(value.longitude) }
      : defaultCenter
  );

  // Use shared Google Maps loader
  const { isLoaded } = useGoogleMaps();

  // Initialize autocomplete
  useEffect(() => {
    if (isLoaded && inputRef.current && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'al' },
          fields: ['formatted_address', 'geometry', 'name', 'address_components']
        }
      );

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const address = place.formatted_address;
          
          let city = '';
          if (place.address_components) {
            const cityComponent = place.address_components.find(
              c => c.types.includes('locality') || c.types.includes('administrative_area_level_1')
            );
            if (cityComponent) {
              city = cityComponent.long_name;
            }
          }

          const newPosition = { lat, lng };
          setInputValue(address);
          setMarkerPosition(newPosition);
          setMapCenter(newPosition);
          
          onChange && onChange({
            address,
            latitude: lat,
            longitude: lng,
            city
          });
        }
      });
    }
  }, [isLoaded, onChange]);

  // Update input value when value.address prop changes
  const prevAddressRef = useRef(value.address);
  useEffect(() => {
    if (value.address !== prevAddressRef.current) {
      prevAddressRef.current = value.address;
      setInputValue(value.address || '');
    }
  }, [value.address]);

  // Update marker position when coordinates change
  const prevCoordsRef = useRef({ lat: value.latitude, lng: value.longitude });
  useEffect(() => {
    if (value.latitude && value.longitude) {
      const newLat = parseFloat(value.latitude);
      const newLng = parseFloat(value.longitude);
      if (newLat !== prevCoordsRef.current.lat || newLng !== prevCoordsRef.current.lng) {
        prevCoordsRef.current = { lat: newLat, lng: newLng };
        const pos = { lat: newLat, lng: newLng };
        setMarkerPosition(pos);
        setMapCenter(pos);
      }
    }
  }, [value.latitude, value.longitude]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // Handle marker drag
  const handleMarkerDragEnd = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    setMarkerPosition({ lat, lng });
    
    // Reverse geocode to get address
    if (isLoaded) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const address = results[0].formatted_address;
          let city = '';
          
          const cityComponent = results[0].address_components?.find(
            c => c.types.includes('locality') || c.types.includes('administrative_area_level_1')
          );
          if (cityComponent) {
            city = cityComponent.long_name;
          }
          
          setInputValue(address);
          onChange && onChange({
            address,
            latitude: lat,
            longitude: lng,
            city
          });
        } else {
          onChange && onChange({
            address: inputValue,
            latitude: lat,
            longitude: lng,
            city: ''
          });
        }
      });
    }
  }, [isLoaded, onChange, inputValue]);

  // Handle map click
  const handleMapClick = useCallback((e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    setMarkerPosition({ lat, lng });
    
    // Reverse geocode
    if (isLoaded) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const address = results[0].formatted_address;
          let city = '';
          
          const cityComponent = results[0].address_components?.find(
            c => c.types.includes('locality') || c.types.includes('administrative_area_level_1')
          );
          if (cityComponent) {
            city = cityComponent.long_name;
          }
          
          setInputValue(address);
          onChange && onChange({
            address,
            latitude: lat,
            longitude: lng,
            city
          });
        }
      });
    }
  }, [isLoaded, onChange]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  if (!isLoaded) {
    return (
      <div className="space-y-3">
        <div className="relative">
          <Input
            value={inputValue}
            placeholder={placeholder}
            disabled
          />
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" />
        </div>
        <div className="w-full h-[250px] bg-zinc-800 rounded-lg flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Address search input */}
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="pl-9"
          data-testid="location-picker-input"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
      </div>
      
      {/* Map */}
      <div className="rounded-lg overflow-hidden border border-white/10">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={markerPosition ? 16 : 12}
          onLoad={onMapLoad}
          onClick={handleMapClick}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false,
            styles: [
              { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
              { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
              { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
              { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
              { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
              { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#263c3f" }] },
              { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b9a76" }] },
              { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
              { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
              { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
              { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
              { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
              { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
              { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f3948" }] },
              { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
              { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
              { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#515c6d" }] },
              { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#17263c" }] }
            ]
          }}
        >
          {markerPosition && (
            <Marker
              position={markerPosition}
              draggable={true}
              onDragEnd={handleMarkerDragEnd}
              animation={window.google.maps.Animation.DROP}
            />
          )}
        </GoogleMap>
      </div>
      
      {/* Coordinates display */}
      {markerPosition && (
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <MapPin className="w-3 h-3" />
          <span>
            {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
          </span>
          <span className="text-zinc-600">|</span>
          <span className="text-zinc-500">Mund ta lëvizni pin-in në hartë</span>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
